package com.solevo.dist.components

import androidx.compose.runtime.*
import com.solevo.dist.navigation.Screen
import com.solevo.dist.theme.JsTheme
import com.solevo.dist.theme.styles.NavigationItemSelectedStyle
import com.solevo.dist.theme.styles.NavigationItemStyle
import com.solevo.dist.util.Constants.COLLAPSED_PANEL_HEIGHT
import com.solevo.dist.util.Constants.SIDE_PANEL_WIDTH
import com.solevo.dist.util.Id
import com.solevo.dist.util.Res
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.solevo.dist.i18n.LocalStrings
import com.varabyte.kobweb.silk.components.icons.fa.FaBars
import com.varabyte.kobweb.silk.components.icons.fa.FaXmark
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

@Composable
fun SidePanel(onMenuClick: () -> Unit) {
    val breakpoint = rememberBreakpoint()
    if (breakpoint > Breakpoint.MD) {
        SidePanelInternal()
    } else {
        CollapsedSidePanel(onMenuClick = onMenuClick)
    }
}

@Composable
private fun SidePanelInternal() {
    Column(
        modifier = Modifier
            .padding(leftRight = 20.px, topBottom = 0.px)
            .width(SIDE_PANEL_WIDTH.px)
            .height(100.vh)
            .position(Position.Fixed)
            .backgroundColor(JsTheme.LightGray.rgb)
            .zIndex(9)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(64.px)
                .margin(bottom = 40.px, left = 10.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Image(
                modifier = Modifier.width(50.px),
                src = Res.Image.logo,
                alt = "Logo Image"
            )
            val strings = LocalStrings.current
            SpanText(
                text = strings.appTitle,
                modifier = Modifier
                    .margin(left = 12.px)
                    .fontFamily("Inter", "sans-serif")
                    .fontSize(18.px)
            )
        }
        NavigationItems()
        Box(Modifier.flexGrow(1))
        SettingsNavigationItem()
    }
}

@Composable
fun NavigationItems() {
    val context = rememberPageContext()

    val strings = LocalStrings.current
    NavigationItem(
        title = strings.navOrders,
        selected = context.route.path == Screen.Orders.route,
        icon = Res.Icon.SlidePanelIcon.orders,
        selectedIcon = Res.Icon.SlidePanelIcon.ordersSelected,
        onClick = { context.router.navigateTo(Screen.Orders.route) }
    )

    NavigationItem(
        title = strings.navQuotes,
        selected = context.route.path == Screen.Quotes.route,
        icon = Res.Icon.SlidePanelIcon.quotes,
        selectedIcon = Res.Icon.SlidePanelIcon.quotesSelected,
        onClick = { context.router.navigateTo(Screen.Quotes.route) }
    )

    NavigationItem(
        title = strings.navStatement,
        selected = context.route.path == Screen.Statement.route,
        icon = Res.Icon.SlidePanelIcon.statement,
        selectedIcon = Res.Icon.SlidePanelIcon.statementSelected,
        onClick = { context.router.navigateTo(Screen.Statement.route) }
    )

    NavigationItem(
        title = strings.navRewards,
        selected = context.route.path == Screen.Rewards.route,
        icon = Res.Icon.SlidePanelIcon.reward,
        selectedIcon = Res.Icon.SlidePanelIcon.rewardSelected,
        onClick = { context.router.navigateTo(Screen.Rewards.route) }
    )
}

@Composable
fun SettingsNavigationItem() {
    val context = rememberPageContext()
    val strings = LocalStrings.current
    
    NavigationItem(
        title = strings.navSettings,
        selected = context.route.path == Screen.Settings.route,
        icon = Res.Icon.SlidePanelIcon.settings,
        selectedIcon = Res.Icon.SlidePanelIcon.settingsSelected,
        onClick = { context.router.navigateTo(Screen.Settings.route) },
        modifier = Modifier.margin(bottom = 20.px)
    )
}

@Composable
private fun NavigationItem(
    modifier: Modifier = Modifier,
    selected: Boolean = false,
    title: String,
    icon: String,
    selectedIcon: String,
    onClick: () -> Unit
) {
    Row(
        modifier = NavigationItemStyle.toModifier()
            .thenIf(selected, NavigationItemSelectedStyle.toModifier())
            .then(modifier)
            .fillMaxWidth()
            .padding(12.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            modifier = Modifier.margin(right = 10.px),
            src = if (selected) selectedIcon else icon,
            alt = "$title Icon"
        )
        SpanText(
            text = title,
            modifier = Modifier
                .id(Id.navigationText)
                .fontFamily("Inter", "sans-serif")
                .fontSize(14.px)
                .thenIf(selected) { Modifier.fontWeight(600) }
        )
    }
}

@Composable
private fun CollapsedSidePanel(onMenuClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(COLLAPSED_PANEL_HEIGHT.px)
            .padding(leftRight = 24.px)
            .backgroundColor(JsTheme.DarkGray.rgb),
        verticalAlignment = Alignment.CenterVertically
    ) {
        FaBars(
            modifier = Modifier
                .margin(right = 24.px)
                .color(Colors.White)
                .cursor(Cursor.Pointer)
                .onClick { onMenuClick() },
            size = IconSize.XL
        )
        Image(
            modifier = Modifier.width(30.px),
            src = Res.Image.logo,
            alt = "Logo Image"
        )
    }
}

@Composable
fun OverflowSidePanel(
    onMenuClose: () -> Unit,
    content: @Composable () -> Unit
) {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()
    val breakpoint = rememberBreakpoint()

    var translateX by remember { mutableStateOf((-100).percent) }
    var opacity by remember { mutableStateOf(0.percent) }

    LaunchedEffect(key1 = breakpoint) {
        translateX = 0.percent
        opacity = 100.percent
        if (breakpoint > Breakpoint.MD) {
            scope.launch {
                translateX = (-100).percent
                opacity = 0.percent
                delay(500)
                onMenuClose()
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(100.vh)
            .position(Position.Fixed)
            .zIndex(9)
            .opacity(opacity)
            .transition(Transition.of(property = "opacity", duration = 300.ms))
            .backgroundColor(JsTheme.White.rgb)
    ) {
        Column(
            modifier = Modifier
                .padding(all = 24.px)
                .fillMaxHeight()
                .width(if (breakpoint < Breakpoint.MD) 50.percent else 25.percent)
                .translateX(translateX)
                .transition(Transition.of(property = "translate", duration = 300.ms))
                .overflow(Overflow.Auto)
                .scrollBehavior(ScrollBehavior.Smooth)
                .backgroundColor(JsTheme.LightGray.rgb)
        ) {
            Row(
                modifier = Modifier.margin(bottom = 60.px, top = 24.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FaXmark(
                    modifier = Modifier
                        .margin(right = 20.px)
                        .color(Colors.DarkGray)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            scope.launch {
                                translateX = (-100).percent
                                opacity = 0.percent
                                delay(500)
                                onMenuClose()
                            }
                        },
                    size = IconSize.LG
                )
                Image(
                    modifier = Modifier
                        .width(30.px)
                        .onClick { context.router.navigateTo(Screen.Orders.route) }
                        .cursor(Cursor.Pointer),
                    src = Res.Image.logo,
                    alt = "Logo Image"
                )
            }
            content()
        }
    }
}